import { type OnChangeFn, type PaginationState } from '@tanstack/react-table';
import { useState } from 'react';

import { DATA_TABLE_DEFAULT_PAGE_SIZE } from './constants';
import type { DataTablePaginationConfig } from './types';

const normalizePageSize = (pageSize: number | undefined): number =>
  pageSize ?? DATA_TABLE_DEFAULT_PAGE_SIZE;

const normalizePageIndex = (pageIndex: number): number =>
  Number.isInteger(pageIndex) && pageIndex >= 0 ? pageIndex : 0;

type UseDataTablePaginationResult = {
  paginationEnabled: boolean;
  resolvedPaginationState: PaginationState | undefined;
  onPaginationChange: OnChangeFn<PaginationState>;
};

const useDataTablePagination = (
  pagination: DataTablePaginationConfig,
): UseDataTablePaginationResult => {
  const paginationEnabled = pagination !== false;
  const paginationConfig =
    paginationEnabled && typeof pagination === 'object' ? pagination : undefined;

  const paginationControlled =
    paginationConfig != null &&
    'paginationState' in paginationConfig &&
    paginationConfig.paginationState != null;
  const controlledOnPaginationChange =
    paginationControlled &&
    paginationConfig != null &&
    'onPaginationChange' in paginationConfig &&
    typeof paginationConfig.onPaginationChange === 'function'
      ? paginationConfig.onPaginationChange
      : undefined;

  const configuredPageSize =
    paginationEnabled && paginationConfig != null && !paginationControlled
      ? normalizePageSize(paginationConfig.pageSize)
      : DATA_TABLE_DEFAULT_PAGE_SIZE;

  const [uncontrolledPaginationState, setUncontrolledPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: configuredPageSize,
  });
  const [prevConfiguredPageSize, setPrevConfiguredPageSize] = useState(configuredPageSize);

  if (paginationEnabled && !paginationControlled && configuredPageSize !== prevConfiguredPageSize) {
    setPrevConfiguredPageSize(configuredPageSize);
    setUncontrolledPaginationState({ pageIndex: 0, pageSize: configuredPageSize });
  }

  const controlledPaginationState =
    paginationEnabled && paginationControlled && paginationConfig != null
      ? {
          pageIndex: normalizePageIndex(paginationConfig.paginationState.pageIndex),
          pageSize: normalizePageSize(paginationConfig.paginationState.pageSize),
        }
      : undefined;

  let resolvedPaginationState;
  if (paginationEnabled) {
    resolvedPaginationState = paginationControlled
      ? (controlledPaginationState ?? {
          pageIndex: 0,
          pageSize: DATA_TABLE_DEFAULT_PAGE_SIZE,
        })
      : uncontrolledPaginationState;
  }

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    if (!paginationEnabled) {
      return;
    }

    if (paginationControlled && controlledOnPaginationChange != null) {
      controlledOnPaginationChange(updater);
      return;
    }

    setUncontrolledPaginationState(updater);
  };

  return {
    paginationEnabled,
    resolvedPaginationState,
    onPaginationChange,
  };
};

export default useDataTablePagination;
